import CircularLoader from 'components/CircularLoader';
import Loader from 'components/Loader';
import NoData from 'components/NoData.tsx';
import ScrollX from 'components/ScrollX.tsx';
import { EndOfListSentinel } from 'components/tables/EndOfListSentinel';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes.tsx';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters.tsx';
import { useCallback, useMemo } from 'react';
import { useLazyGetClassifiersWithFiltersQuery } from 'store/api/classifiers';
import { Classifier } from 'types/classifiers';
import { QueryResponse } from 'types/paginationAndFilter.ts';
import ClassifierItem from './ClassifierItem.tsx';

const ClassifiersList = () => {
    const [fetchData, { data, isFetching }] = useLazyGetClassifiersWithFiltersQuery();

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'code' }, { selector: 'values.code' }, { selector: 'values.texts.value' }],
            passiveFilter: 'code!=COUNTRY',
        }),
        []
    );

    const renderClassifiersTable = useCallback(
        (
            dataToDisplay: Classifier[],
            initialLoad: boolean,
            noData: boolean,
            textId?: string,
            paginationProps?: Partial<QueryResponse<Classifier>>,
            handleLoadMore?: () => void
        ) => {
            const classifierArray = dataToDisplay ?? [];

            if (initialLoad) {
                return <Loader />;
            }

            return (
                <ScrollX>
                    <div className="flex flex-col gap-2 pr-2">
                        {isFetching && classifierArray.length > 0 ? (
                            <CircularLoader className="bg-divider/40 z-12" />
                        ) : null}
                        {noData ? (
                            <NoData titleId={textId} />
                        ) : (
                            classifierArray.map((classifier: Classifier) => (
                                <ClassifierItem key={classifier.code} classifier={classifier} />
                            ))
                        )}
                        {/* This list does not own its scroller — the settings layout does — so the
                            sentinel resolves the scrolling ancestor itself. It still has to sit at the
                            END of the list content, inside that scroller, to be clipped until the
                            user actually reaches the last item. */}
                        <EndOfListSentinel
                            rowCount={classifierArray.length}
                            hasNextPage={!!paginationProps && !paginationProps.last}
                            isLoadingMore={isFetching}
                            onLoadMore={handleLoadMore}
                        />
                    </div>
                </ScrollX>
            );
        },
        [isFetching]
    );

    return (
        <ListTableWithFilters<Classifier>
            data={data}
            isLoading={isFetching}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            renderCustomTable={renderClassifiersTable}
        />
    );
};

export default ClassifiersList;
